/**
 * kit-haux.js
 * Web Component — Monte seu Kit (bloco dentro de main-product)
 * Vanilla JS, sem dependências.
 */
(function () {
  'use strict';

  class KitHauxBuilder extends HTMLElement {
    constructor() {
      super();
      this._state = {};
      this._config = { tiers: [], minItems: 1, bundleId: 'bundle-kit' };
      this._refs = {};
    }

    connectedCallback() {
      this._loadConfig();
      this._cacheRefs();
      this.addEventListener('click', this._onClick.bind(this));
      this._render();
    }

    _loadConfig() {
      const blockId = this.dataset.blockId;
      let script = this.querySelector('.bb-product-data');
      if (!script && blockId) {
        script = document.querySelector(`.bb-product-data[data-for-block="${blockId}"]`);
      }
      if (!script) return;
      try {
        const cfg = JSON.parse(script.textContent);
        if (!Array.isArray(cfg.tiers)) cfg.tiers = [];
        cfg.tiers.sort((a, b) => a.qty - b.qty);
        // Cap máximo de itens = qty do último tier (igual referência Liquidz, fixo em 4 via schema)
        cfg.maxItems = cfg.tiers.length ? cfg.tiers[cfg.tiers.length - 1].qty : 4;
        // Trava de quantidade máxima (schema). Se desativada, o usuário pode passar do maior tier.
        // Default true para não alterar comportamento de lojas que ainda não têm a chave.
        if (typeof cfg.enableMaxLimit === 'undefined') cfg.enableMaxLimit = true;
        this._config = cfg;
      } catch (e) {
        console.error('[KitHaux] config inválida:', e);
      }
      this._confettiFired = false;
    }

    _cacheRefs() {
      this._refs = {
        cards:            [...this.querySelectorAll('.bb-card')],
        tiers:            [...this.querySelectorAll('.bb-tier')],
        connectorFills:   [...this.querySelectorAll('[data-connector-fill]')],
        progressWrap:     this.querySelector('[data-progress-wrap]'),
        progressLabel:    this.querySelector('[data-progress-label]'),
        summary:          this.querySelector('[data-summary]'),
        summaryQty:       this.querySelector('[data-summary-qty]'),
        summaryQtyLabel:  this.querySelector('[data-summary-qty-label]'),
        summaryQtyWrap:   this.querySelector('[data-summary-qty-wrap]'),
        summaryDiscPill:  this.querySelector('[data-summary-discount-pill]'),
        summaryPrices:    this.querySelector('[data-summary-prices]'),
        summaryOriginal:  this.querySelector('[data-summary-original]'),
        summaryFinal:     this.querySelector('[data-summary-final]'),
        finishBtn:        this.querySelector('[data-action="checkout"]'),
        finishLabel:      this.querySelector('[data-finish-label]'),
        finishIcon:       this.querySelector('[data-finish-icon]'),
        shipping:         this.querySelector('[data-shipping]'),
      };
      this._emptyLabel = '';
    }

    _onClick(e) {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === 'checkout') { this._checkout(); return; }
      const card = btn.closest('.bb-card');
      if (!card) return;
      const id = card.dataset.variantId;
      if (action === 'add' || action === 'increase') this._inc(id);
      else if (action === 'decrease') this._dec(id);
    }

    _inc(id) {
      // Cap rígido só quando a trava está ativada no schema
      if (this._config.enableMaxLimit && this._total() >= this._config.maxItems) return;
      this._state[id] = (this._state[id] || 0) + 1;
      this._render();
    }
    _dec(id) {
      if (!this._state[id]) return;
      this._state[id]--;
      if (this._state[id] <= 0) delete this._state[id];
      this._render();
    }

    _total() { return Object.values(this._state).reduce((s, n) => s + n, 0); }

    _activeTier() {
      const total = this._total();
      let active = null;
      for (const t of this._config.tiers) if (total >= t.qty) active = t;
      return active;
    }

    _originalCents() {
      let total = 0;
      for (const card of this._refs.cards) {
        const qty = this._state[card.dataset.variantId] || 0;
        if (qty) total += (parseInt(card.dataset.variantPrice, 10) || 0) * qty;
      }
      return total;
    }

    _money(cents) { return 'R$ ' + (cents / 100).toFixed(2).replace('.', ','); }

    _render() {
      this._renderCards();
      this._renderTiers();
      this._renderProgress();
      this._renderSummary();
      this._maybeFireConfetti();
    }

    _maybeFireConfetti() {
      const total = this._total();
      const max = this._config.maxItems || 4;
      if (total >= max && !this._confettiFired) {
        this._confettiFired = true;
        this._fireConfetti();
      } else if (total < max && this._confettiFired) {
        // permite re-disparar se o usuário remover e adicionar de novo
        this._confettiFired = false;
      }
    }

    _fireConfetti() {
      const root = this.querySelector('.bb-root') || this;
      const host = document.createElement('div');
      host.className = 'bb-confetti';
      host.setAttribute('aria-hidden', 'true');
      const colors = ['#059F77', '#D49627', '#E94B6E', '#3B82F6', '#FACC15', '#A855F7', '#10B981'];
      const count = 80;
      for (let i = 0; i < count; i++) {
        const p = document.createElement('span');
        p.className = 'bb-confetti__piece';
        const left = Math.random() * 100;
        const delay = Math.random() * 0.4;
        const dur = 1.6 + Math.random() * 1.2;
        const drift = (Math.random() - 0.5) * 200;
        const rot = Math.random() * 720;
        const size = 6 + Math.random() * 6;
        p.style.left = left + '%';
        p.style.background = colors[i % colors.length];
        p.style.width = size + 'px';
        p.style.height = (size * 0.6) + 'px';
        p.style.animationDelay = delay + 's';
        p.style.animationDuration = dur + 's';
        p.style.setProperty('--bb-confetti-x', drift + 'px');
        p.style.setProperty('--bb-confetti-rot', rot + 'deg');
        host.appendChild(p);
      }
      root.appendChild(host);
      setTimeout(() => host.remove(), 3500);
    }

    _renderCards() {
      const total = this._total();
      // Lock visual só quando a trava está ativada; desativada nunca trava os botões
      const capped = this._config.enableMaxLimit && total >= this._config.maxItems;
      const tiers = this._config.tiers || [];
      // Tier que dirige o preço cortado mostrado em TODAS as cards:
      // - se há próximo tier alcançável → usa ele (incentivo "vale a pena adicionar mais 1")
      // - se já está no cap → usa o tier atual (último alcançado)
      const active = this._activeTier();
      const next = tiers.find(t => t.qty > total);
      const incentiveTier = next || active || (tiers.length ? tiers[0] : null);
      for (const card of this._refs.cards) {
        const qty = this._state[card.dataset.variantId] || 0;
        const counter = card.querySelector('[data-counter]');
        const addBtn  = card.querySelector('[data-action="add"]');
        const incBtn  = card.querySelector('[data-action="increase"]');
        const qtyEl   = card.querySelector('[data-qty]');
        const priceWrap   = card.querySelector('.bb-card__price');
        const priceFinal  = card.querySelector('[data-price-final]');
        const isAvailable = card.dataset.available === 'true';

        // Preço cortado em TODAS as cards disponíveis (mesmo sem qty selecionada)
        if (priceWrap && priceFinal && isAvailable && incentiveTier && incentiveTier.discount > 0) {
          const unitCents = parseInt(card.dataset.variantPrice, 10) || 0;
          const discountedCents = Math.round(unitCents * (1 - incentiveTier.discount / 100));
          priceFinal.textContent = this._money(discountedCents);
          priceFinal.hidden = false;
          priceWrap.classList.add('bb-card__price--discounted');
        } else if (priceWrap && priceFinal) {
          priceFinal.hidden = true;
          priceWrap.classList.remove('bb-card__price--discounted');
        }

        // Toggle counter/botão Adicionar conforme qty
        if (qty > 0) {
          if (counter) counter.hidden = false;
          if (addBtn)  addBtn.hidden  = true;
          if (qtyEl)   qtyEl.textContent = qty;
          card.classList.add('bb-card--selected');
        } else {
          if (counter) counter.hidden = true;
          if (addBtn)  addBtn.hidden  = false;
          card.classList.remove('bb-card--selected');
        }
        // Lock visual: card sem qty quando cap atingido vira "locked" (cinza, sem clique)
        const shouldLock = capped && qty === 0 && isAvailable;
        card.classList.toggle('bb-card--locked', shouldLock);
        if (addBtn) {
          addBtn.disabled = shouldLock;
          addBtn.setAttribute('aria-disabled', shouldLock ? 'true' : 'false');
        }
        // Lock do botão "+" do counter quando cap atingido (mesmo nas cards selecionadas)
        if (incBtn) {
          incBtn.disabled = capped;
          incBtn.setAttribute('aria-disabled', capped ? 'true' : 'false');
        }
      }
    }

    _renderTiers() {
      const total = this._total();
      for (const t of this._refs.tiers) {
        t.classList.toggle('bb-tier--active', total >= parseInt(t.dataset.tierQty, 10));
      }
    }

    _renderProgress() {
      const { connectorFills, progressLabel } = this._refs;
      const tiers = this._config.tiers;
      const total = this._total();

      // Preenchimento dos conectores entre tiers (curva de incentivo)
      // Conector i conecta tier[i] → tier[i+1]
      if (connectorFills && connectorFills.length) {
        for (let i = 0; i < connectorFills.length; i++) {
          const fill = connectorFills[i];
          const t = tiers[i];
          const next = tiers[i + 1];
          let pct = 0;
          if (!t) {
            pct = 0;
          } else if (!next) {
            // último tier não tem conector "depois"; só preenche se já alcançado
            pct = total >= t.qty ? 100 : 0;
          } else if (total >= next.qty) {
            pct = 100;
          } else if (total < t.qty) {
            pct = 0;
          } else {
            // conector ativo: aplica curva de incentivo (sempre "puxando" pro próximo tier)
            const span = next.qty - t.qty;            // ex: 1 (entre 1 e 2)
            const progress = (total - t.qty + 1) / (span + 1); // ratio linear no intervalo
            pct = Math.min(100, Math.pow(progress, 0.3) * 100);
          }
          fill.style.width = pct + '%';
        }
      }

      // Texto do label (mesma lógica de antes)
      const { progressWrap } = this._refs;
      if (!progressLabel) return;
      const active = this._activeTier();
      if (total === 0) {
        // Sem itens: esconde a barra de mensagem inteira (não há "Adicione itens...")
        if (progressWrap) progressWrap.hidden = true;
        progressLabel.classList.remove('bb-progress-label--active');
      } else {
        if (progressWrap) progressWrap.hidden = false;
        const nxt = tiers.find(t => t.qty > total);
        if (active && nxt) progressLabel.textContent = `${active.discount}% off — adicione mais ${nxt.qty - total} para ${nxt.discount}%`;
        else if (active) progressLabel.textContent = `${active.discount}% de desconto aplicado!`;
        else if (nxt) progressLabel.textContent = `Adicione mais ${nxt.qty - total} para ${nxt.discount}% off`;
        progressLabel.classList.toggle('bb-progress-label--active', !!active);
      }
    }

    _renderSummary() {
      const r = this._refs;
      if (!r.summary) return;
      const total = this._total();
      const active = this._activeTier();
      const tiers = this._config.tiers || [];
      const min = this._config.minItems || 1;
      const isEmpty = total < min;

      // Pill de desconto (canto esquerdo)
      // Estado vazio: pill mostra desconto do primeiro tier alcançável (incentivo)
      // Estado ativo: pill mostra desconto efetivo do tier alcançado
      if (r.summaryDiscPill) {
        let pillDiscount = 0;
        if (isEmpty) {
          const firstTier = tiers[0];
          if (firstTier) pillDiscount = firstTier.discount;
        } else if (active) {
          pillDiscount = active.discount;
        }
        if (pillDiscount > 0) {
          r.summaryDiscPill.textContent = `GANHE ${pillDiscount}% OFF`;
          r.summaryDiscPill.hidden = false;
        } else {
          r.summaryDiscPill.hidden = true;
        }
      }

      // Calcular preços
      let orig, finalCents, showCrossedOriginal;
      if (isEmpty) {
        // Estado vazio: preview baseado em 1 unidade da primeira variante disponível,
        // mostrando o desconto do tier 1 como incentivo
        const firstAvailableCard = this._refs.cards.find(c => c.dataset.available === 'true');
        const firstTier = tiers[0];
        if (firstAvailableCard && firstTier) {
          orig = parseInt(firstAvailableCard.dataset.variantPrice, 10) || 0;
          finalCents = Math.round(orig * (1 - firstTier.discount / 100));
          showCrossedOriginal = true;
        } else {
          orig = 0;
          finalCents = 0;
          showCrossedOriginal = false;
        }
      } else {
        // Estado ativo: cálculo real do kit
        orig = this._originalCents();
        finalCents = active ? Math.round(orig * (1 - active.discount / 100)) : orig;
        showCrossedOriginal = !!active;
      }

      // Quantidade só aparece com itens; preços aparecem SEMPRE (preview no vazio)
      if (r.summaryQtyWrap) r.summaryQtyWrap.hidden = isEmpty;
      if (r.summaryPrices) r.summaryPrices.hidden = finalCents === 0;

      if (r.summaryQty) r.summaryQty.textContent = total;
      if (r.summaryQtyLabel) r.summaryQtyLabel.textContent = total === 1 ? 'item' : 'itens';
      if (r.summaryOriginal) r.summaryOriginal.textContent = showCrossedOriginal ? this._money(orig) : '';
      if (r.summaryFinal) r.summaryFinal.textContent = finalCents > 0 ? this._money(finalCents) : '';

      // Botão: estado empty (cinza, texto "selecione") vs ativo (verde, "FINALIZAR — R$ X")
      if (r.finishBtn) {
        const labelDefault = r.finishBtn.dataset.finishDefault || 'FINALIZAR COMPRA';
        const labelEmpty = r.finishBtn.dataset.finishEmpty || 'SELECIONE PELO MENOS 1 UNIDADE';
        if (isEmpty) {
          r.finishBtn.classList.add('bb-finish-btn--empty');
          r.finishBtn.disabled = true;
          r.finishBtn.style.opacity = '';
          r.finishBtn.style.pointerEvents = '';
          if (r.finishLabel) r.finishLabel.textContent = labelEmpty;
          if (r.finishIcon) r.finishIcon.hidden = true;
        } else {
          r.finishBtn.classList.remove('bb-finish-btn--empty');
          r.finishBtn.disabled = false;
          r.finishBtn.style.opacity = '';
          r.finishBtn.style.pointerEvents = '';
          if (r.finishLabel) r.finishLabel.textContent = `${labelDefault} — ${this._money(finalCents)}`;
          if (r.finishIcon) r.finishIcon.hidden = false;
        }
      }

      // Frete grátis: mostra quando preço final REAL atinge o threshold
      // (só pode ativar no estado ativo; preview no estado vazio não conta)
      if (r.shipping) {
        const threshold = this._config.freeShippingThreshold || 0;
        const eligible = !isEmpty && threshold > 0 && finalCents >= threshold;
        r.shipping.hidden = !eligible;
      }
    }

    async _checkout() {
      const items = Object.entries(this._state)
        .filter(([, q]) => q > 0)
        .map(([id, q]) => ({
          id: parseInt(id, 10),
          quantity: q,
          properties: { _bundle_id: this._config.bundleId || 'bundle-kit' },
        }));
      if (this._total() < (this._config.minItems || 1) || !items.length) return;
      const btn = this._refs.finishBtn;
      if (btn) { btn.classList.add('bb-finish-btn--loading'); btn.querySelector('svg')?.remove(); }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Erro ao adicionar');
        }
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[KitHaux] erro no checkout:', err);
        alert('Ops, algo deu errado ao montar seu kit. Tente novamente.');
        if (btn) btn.classList.remove('bb-finish-btn--loading');
      }
    }
  }

  if (!customElements.get('kit-haux-builder')) {
    customElements.define('kit-haux-builder', KitHauxBuilder);
  }
})();